/**
 * templateLinkConfig
 *
 * Shared reader-side Link_Config contract and resolvers, generalized from the
 * local `normalizeWaasChromeLink` / `resolveWaasChromeHref` / `waasChromeLinkProps`
 * helpers in `WaasTemplateChrome.jsx`. This is the single source of truth used by
 * header nav items, footer links, buttons, and link-converted elements so the
 * published reader navigates identically to the builder preview.
 *
 * Link_Config shape:
 *   {
 *     type: 'internal' | 'external' | 'document', // default 'internal'
 *     url: string,                                 // '/about' or 'https://…'
 *     target: '_self' | '_blank',                  // default '_self'
 *     documentAction?: 'open' | 'download'         // only meaningful for 'document'
 *   }
 *
 * All inputs are treated as "no override" when null/invalid: the resolved config
 * defaults to `type: 'internal'`, `target: '_self'`. The `newTab` convenience
 * boolean stays in sync with `target` (newTab true => target '_blank').
 */

const EXTERNAL_PREFIX = /^(https?:|mailto:|tel:|www\.)/i;
const PROTOCOL_PREFIX = /^(https?:|mailto:|tel:)/i;

/**
 * Normalize a navigable source into a Link_Config.
 *
 * Accepts either a wrapper object (`{ link, newTab, url, route }`, as used by the
 * chrome) or a bare Link_Config (`{ type, url, target, documentAction }`). Null or
 * non-object input is treated as "no override".
 *
 * @param {object|null|undefined} source
 * @param {string} [fallbackUrl]
 * @returns {{ type: 'internal'|'external'|'document', url: string, target: '_self'|'_blank', documentAction: 'open'|'download' }}
 */
export function normalizeLinkConfig(source = {}, fallbackUrl = '') {
  const src = source && typeof source === 'object' ? source : {};
  // A Link_Config may be nested under `.link` (wrapper usage) or be the source itself.
  const link = src.link && typeof src.link === 'object'
    ? src.link
    : (('type' in src || 'documentAction' in src) ? src : {});
  const fallback = fallbackUrl == null ? '' : fallbackUrl;

  const type = link.type
    || (fallback && EXTERNAL_PREFIX.test(String(fallback)) ? 'external' : 'internal');
  const url = link.url ?? fallback ?? src.url ?? src.route ?? '';
  const target = link.target || (src.newTab ? '_blank' : '_self');
  const documentAction = link.documentAction || 'open';

  return { type, url, target, documentAction };
}

/**
 * Resolve a Link_Config to an href string for rendering.
 *
 * - internal links resolve to a leading-slash path
 * - external links resolve to a protocol-qualified URL (https:// is prepended when
 *   the value has no scheme and does not already begin with `//`)
 * - empty values resolve to '#'
 *
 * @param {object|null|undefined} linkConfig
 * @param {string} [fallbackUrl]
 * @returns {string}
 */
export function resolveLinkHref(linkConfig, fallbackUrl = '') {
  const link = normalizeLinkConfig(linkConfig, fallbackUrl);
  const url = String(link.url || '').trim();
  if (!url) return '#';
  if (link.type === 'external' && !PROTOCOL_PREFIX.test(url) && !url.startsWith('//')) {
    return `https://${url.replace(/^\/+/, '')}`;
  }
  if (link.type === 'internal' && url !== '#' && !url.startsWith('/') && !url.startsWith('#')) {
    return `/${url.replace(/^\/+/, '')}`;
  }
  return url;
}

/**
 * Resolve the props consumed by `TemplateRouterLink` (and plain anchors).
 *
 * `rel` is set to 'noopener noreferrer' whenever the link opens in a new tab.
 * `download` is set only for `type: 'document'` with `documentAction: 'download'`.
 *
 * @param {object|null|undefined} linkConfig
 * @param {string} [fallbackUrl]
 * @returns {{ to: string, target: string|undefined, rel: string|undefined, download: string|undefined }}
 */
export function linkProps(linkConfig, fallbackUrl = '') {
  const link = normalizeLinkConfig(linkConfig, fallbackUrl);
  const target = link.target === '_blank' ? '_blank' : undefined;
  return {
    to: resolveLinkHref(linkConfig, fallbackUrl),
    target,
    rel: target ? 'noopener noreferrer' : undefined,
    download: link.type === 'document' && link.documentAction === 'download' ? '' : undefined
  };
}

/**
 * Validate a route value entered for a navigable target.
 *
 * Invalid when:
 *  - an internal path is empty, or
 *  - an external URL cannot be parsed.
 *
 * When `type` is omitted, it is inferred from the value (external when it carries a
 * scheme/`www.` prefix, internal otherwise).
 *
 * @param {string} value
 * @param {'internal'|'external'} [type]
 * @returns {{ ok: boolean, message: string }}
 */
export function validateRoute(value, type) {
  const raw = value == null ? '' : String(value).trim();
  const kind = type === 'external' || type === 'internal'
    ? type
    : (EXTERNAL_PREFIX.test(raw) ? 'external' : 'internal');

  if (kind === 'internal') {
    if (!raw) {
      return { ok: false, message: 'Enter an internal path such as /about.' };
    }
    return { ok: true, message: '' };
  }

  // external
  if (!raw) {
    return { ok: false, message: 'Enter a URL such as https://example.com.' };
  }
  const candidate = PROTOCOL_PREFIX.test(raw)
    ? raw
    : (raw.startsWith('//') ? `https:${raw}` : `https://${raw.replace(/^\/+/, '')}`);
  try {
    // eslint-disable-next-line no-new
    new URL(candidate);
    return { ok: true, message: '' };
  } catch (err) {
    return { ok: false, message: 'Enter a valid URL such as https://example.com.' };
  }
}

const templateLinkConfig = {
  normalizeLinkConfig,
  resolveLinkHref,
  linkProps,
  validateRoute
};

export default templateLinkConfig;
